/**
 * @file Dieses Skript misst die Scroll-Tiefe des Benutzers auf der Seite und innerhalb des Hauptartikels.
 * Es erfasst die maximale Scroll-Position und sendet diese Daten an ein Analytics-Backend (Piano).
 * Die Implementierung ist auf Performance optimiert, um negative Auswirkungen auf das Benutzererlebnis zu vermeiden,
 * insbesondere durch die Verwendung von `requestAnimationFrame` für Scroll-Events und eine effiziente Höhenberechnung.
 */
(function () {

  // Leere Funktion, die als Standardwert für zurückgesetzte "lastReported"-Variablen dient.
  var noop = (function () {
  })();

  /**
   * Berechnet die tatsächliche visuelle Höhe eines Elements.
   * Diese Funktion ist besonders wichtig für Elemente mit `display: contents`, da diese selbst keine
   * geometrischen Abmessungen haben und ihre Höhe von den gerenderten Kind-Elementen abhängt.
   *
   * @param {HTMLElement} element Das Element, dessen Höhe gemessen werden soll.
   * @returns {number} Die berechnete Höhe in Pixeln.
   */
  var getOffsetHeight = function getOffsetHeight(element) {
    var computedStyle = window.getComputedStyle(element);

    // Wenn das Element nicht `display: contents` ist, ist sein `offsetHeight` ein verlässlicher Wert.
    if (computedStyle.display !== 'contents') {
      return element.getBoundingClientRect().height;
    }

    // Für `display: contents` müssen wir die Grenzen der gerenderten Kind-Elemente messen.
    // Dieser Ansatz minimiert "Layout-Trashing" (mehrfache, erzwungene Layout-Neuberechnungen),
    // indem alle DOM-Lesezugriffe gebündelt werden.
    if (computedStyle.display === 'contents') {
      // 1. Alle Nachfahren-Elemente einmalig abrufen.
      var descendants = element.querySelectorAll('*');
      if (descendants.length === 0) {
        return 0;
      }

      // 2. DOM-Lesezugriffe bündeln: Alle Bounding-Rechtecke auf einmal abrufen.
      // Dies erzwingt nur eine einzige Layout-Neuberechnung (Reflow) und ist sehr effizient.
      var rects = Array.from(descendants).map(function (el) {
        return el.getBoundingClientRect();
      });

      // 3. Die gesammelten Daten in reinem JavaScript weiterverarbeiten, ohne weiteren DOM-Zugriff.
      //    Herausfiltern von Elementen, die keinen Platz einnehmen (Breite oder Höhe ist 0).
      var visibleRects = rects.filter(function (rect) {
        return rect.width > 0 || rect.height > 0;
      });
      if (visibleRects.length === 0) return 0;

      // 4. Die Gesamthöhe aus der minimalen 'top'- und maximalen 'bottom'-Position aller sichtbaren Kinder berechnen.
      var minTop = Math.min.apply(null, visibleRects.map(function (rect) {
        return rect.top;
      }));
      var maxBottom = Math.max.apply(null, visibleRects.map(function (rect) {
        return rect.bottom;
      }));

      return maxBottom - minTop;
    }
  }

  // Zustandsvariablen für das Tracking der Scroll-Position.
  var hasScrolled = false,
    scrollMaximumArticle = 0,
    scrollMaximumDocument = 0,
    scrollMaximumDepth = 0,
    lastReportedScrollMaximumArticle,
    lastReportedScrollMaximumDocument,
    lastReportedScrollDepth;

  // Das Hauptelement des Artikels auswählen. Fallbacks sind vorgesehen.
  var article =
    document.querySelector('article .detail-body') ||
    document.querySelector('#content-wrapper') ||
    document.body;

  // Die Gesamtlänge des Artikels und des Dokuments beim Laden der Seite berechnen.
  var articleLength = getOffsetHeight(article);
  var documentLength = (document.documentElement.scrollHeight - document.documentElement.offsetHeight);

  /**
   * Prüft, ob im Artikel eingebettete Inhalte vorhanden sind.
   * @returns {boolean} True, wenn eingebettete Inhalte gefunden wurden.
   */
  var hasEmbededContents = function () {
    return (0 !== article.querySelectorAll('[data-spy=embed]').length);
  }

  /**
   * Berechnet die aktuelle Scroll-Tiefe und aktualisiert die Maximalwerte.
   * Diese Funktion wird innerhalb von `requestAnimationFrame` aufgerufen, um die Performance zu schonen.
   */
  var _scrollReport = function () {
    // Aktuelle Scroll-Position ermitteln.
    var scrollDepth =
      window.scrollY ||
      document.documentElement.scrollTop ||
      document.body.scrollTop;

    /* Calculate the percentage of the article that has been scrolled */
    var scrollPercentArticle = Math.round((scrollDepth / articleLength) * 100) / 100;
    var scrollPercentDocument = Math.round((scrollDepth / documentLength) * 100) / 100

    // Wenn der prozentuale Scroll-Wert höher ist als das bisherige Maximum, wird der neue Maximalwert gesetzt (begrenzt auf 1).
    scrollMaximumArticle =
      scrollPercentArticle > scrollMaximumArticle
        ? scrollPercentArticle > 1
          ? 1
          : scrollPercentArticle
        : scrollMaximumArticle;

    // Dasselbe für die prozentuale Scroll-Tiefe des gesamten Dokuments.
    scrollMaximumDocument =
      scrollPercentDocument > scrollMaximumDocument
        ? scrollPercentDocument > 1
          ? 1
          : scrollPercentDocument
        : scrollMaximumDocument;

    // Maximale Scroll-Tiefe in Pixeln speichern.
    scrollMaximumDepth = scrollDepth > scrollMaximumDepth ? scrollDepth : scrollMaximumDepth

    hasScrolled = false;
  };

  /**
   * Event-Handler für das 'scroll'-Event.
   * Nutzt `requestAnimationFrame`, um die `_scrollReport`-Funktion nur einmal pro Frame auszuführen,
   * was das "Throttling" des Scroll-Events sicherstellt und die Performance verbessert.
   */
  var _onScroll = function _onScroll() {
    if (!hasScrolled) {
      window.requestAnimationFrame(_scrollReport);
    }
    hasScrolled = true;
  };
  // Scroll-Event-Listener mit `passive: true` für bessere Scroll-Performance.
  window.addEventListener('scroll', _onScroll, {
    passive: true
  });

  /**
   * Sendet die erfassten Scroll-Daten, wenn sich die Werte geändert haben.
   * Behandelt auch die Events `beforeunload` und `visibilitychange`, um Daten vor dem Verlassen der Seite zu senden.
   * @param {Event} event Das auslösende Event-Objekt.
   */
  var report = function (event) {
    if (!PIANO.Utils.isEnabled()) {
      return;
    }

    // Push the payload to dataLayer, and only push valid time values
    if (
      (lastReportedScrollMaximumArticle !== scrollMaximumArticle) ||
      (lastReportedScrollMaximumDocument !== scrollMaximumDocument) ||
      (lastReportedScrollDepth !== scrollMaximumDepth)
    ) {
      var data = {
        'scroll_depth_article': scrollMaximumArticle,
        'scroll_depth_page': scrollMaximumDocument,
        'scroll_length_article_px': articleLength,
        'scroll_depth_page_px': scrollMaximumDepth,
        'has_embed': hasEmbededContents()
      };

      // console.debug('Tracking Piano scrollDepth: sendEvent, page.scroll', event, data)

      _paq.push(['sendEvent', 'page.scroll', data]);

      lastReportedScrollMaximumArticle = scrollMaximumArticle;
      lastReportedScrollMaximumDocument = scrollMaximumDocument;
      lastReportedScrollDepth = scrollMaximumDepth;
    }

    // Behandelt das Verlassen der Seite.
    if (event && 'beforeunload' === event.type) {
      // console.debug('Tracking Piano scrollDepth: beforeunload');
      if (window.unloaded) {
        return;
      }

      scrollMaximumArticle = 0;
      scrollMaximumDocument = 0;
      scrollMaximumDepth = 0;
      lastReportedScrollMaximumArticle = noop;
      lastReportedScrollMaximumDocument = noop;
      window.unloaded = true;

      /* Remove the listeners from the window */
      window.removeEventListener('beforeunload', report);
    }

    // Behandelt das Wechseln von Tabs oder das Minimieren des Browsers.
    if (event && 'visibilitychange' === event.type) {
      // Wenn der Tab in den Hintergrund wechselt, wird der Zustand als "entladen" markiert.
      if ('hidden' === document.visibilityState) {
        if (window.unloaded) {
          return;
        }
        window.unloaded = true;
        // console.debug('Tracking Piano scrollDepth: visibility change hidden');
      }

      // Wenn der Benutzer zum Tab zurückkehrt, wird der Zustand zurückgesetzt.
      if ('visible' === document.visibilityState) {
        if (!window.unloaded) return;
        window.unloaded = false;
        // console.debug('Tracking Piano scrollDepth: visibility change visible');

        // Wenn der Benutzer länger als 30 Minuten inaktiv war, wird ein neuer Pageview ausgelöst.
        if ((Date.now() - window.startTime) / 1000 > 1800) {
          // console.debug('Tracking Piano scrollDepth: trigger `piano:pageview`');

          PIANO.Utils.trigger('pageview');

          window.startTime = Date.now();
        }
      }
    }
  };

  // Listener für SPA (Single Page Application) Navigation, um das Tracking zurückzusetzen.
  document.addEventListener('piano:beforeReplace', function () {
    // console.debug('Tracking Piano scrollDepth: on beforeReplace');
    report({
      type: 'beforeReplace'
    });
  });
  document.addEventListener('piano:pushstate', function () {
    // console.debug('Tracking Piano scrollDepth: on pushstate');

    scrollMaximumArticle = 0;
    scrollMaximumDocument = 0;
    scrollMaximumDepth = 0;
    lastReportedScrollMaximumArticle = noop;
    lastReportedScrollMaximumDocument = noop;
    hasScrolled = false;

    // Artikel-Element nach der Navigation neu auswählen.
    article =
      document.querySelector('article .detail-body') ||
      document.querySelector('#content-wrapper') ||
      document.body;

    // Längen neu berechnen.
    articleLength = getOffsetHeight(article);
    documentLength = document.documentElement.scrollHeight;
  });

  // Event-Listener, um den Report beim Verlassen der Seite oder bei Inaktivität zu senden.
  window.addEventListener('beforeunload', report);
  window.addEventListener('visibilitychange', report);

})();
